import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useCart } from '../context/CartContext';
import { CheckCircle, Loader2, XCircle, ArrowRight, Package } from 'lucide-react';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderInfo, setOrderInfo] = useState(null);
  const { refreshCart } = useCart();
  
  const sessionId = searchParams.get('session_id');
  const cartCheckoutId = searchParams.get('cart_checkout_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await api.get(`/cart/checkout-status/${sessionId}`);
        const data = response.data;
        
        if (data.status === 'paid') {
          setStatus('success');
          setOrderInfo(data);
          refreshCart(); // Refresh cart to show it's empty
        } else if (data.status === 'unpaid' || data.status === 'expired') {
          setStatus('error');
        } else {
          setStatus('pending');
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkStatus();
    
    // Poll for status if still pending
    const interval = setInterval(() => {
      if (status === 'pending' || status === 'loading') {
        checkStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [sessionId, status, refreshCart]);

  return (
    <div className="min-h-screen bg-dark text-white">
      <Navbar />
      
      <main className="container mx-auto px-4 py-20">
        <motion.div
          {...fadeInUp}
          className="max-w-lg mx-auto text-center"
        >
          {status === 'loading' && (
            <div className="py-12">
              <Loader2 className="w-16 h-16 animate-spin text-lime mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Processing your order...</h1>
              <p className="text-[#A1A1AA]">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-12">
              <div className="w-20 h-20 bg-lime/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-lime" />
              </div>
              <h1 className="text-3xl font-bold mb-4">Order Confirmed!</h1>
              <p className="text-[#A1A1AA] mb-2">
                Thank you for your purchase! Your {orderInfo?.order_count || 'dubplate'} order(s) have been placed.
              </p>
              <p className="text-[#A1A1AA] mb-8">
                The artists will review your requests and you'll receive a notification when they respond.
              </p>
              
              <div className="bg-surface border border-[#262626] rounded-xl p-6 mb-8">
                <div className="flex items-center justify-center gap-3 text-lime mb-2">
                  <Package className="w-5 h-5" />
                  <span className="font-semibold">What's Next?</span>
                </div>
                <ul className="text-sm text-[#A1A1AA] space-y-2 text-left">
                  <li>• Artists will accept or decline within 48 hours</li>
                  <li>• Once accepted, you can message them directly</li>
                  <li>• Delivery within their estimated time (up to 45 days)</li>
                  <li>• Download your dubplate when ready!</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/dashboard" className="btn-primary inline-flex items-center justify-center gap-2">
                  View My Orders <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/artists" className="btn-secondary inline-flex items-center justify-center gap-2">
                  Continue Shopping
                </Link>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="py-12">
              <Loader2 className="w-16 h-16 animate-spin text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Payment Processing</h1>
              <p className="text-[#A1A1AA]">Your payment is still being processed. This page will update automatically.</p>
            </div>
          )}

          {status === 'error' && (
            <div className="py-12">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
              <p className="text-[#A1A1AA] mb-8">We couldn't verify your payment. Please contact support if you were charged.</p>
              <Link to="/cart" className="btn-primary inline-flex items-center gap-2">
                Return to Cart <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
